import type { Result } from "@pdfking/errors"
import { Failure, Success } from "@pdfking/errors"

/**
 * Wraps a promise to prevent it from throwing errors.
 * Returns a Result type with either a value or an error, never throws.
 *
 * @param promise - The promise to wrap
 * @returns A promise that always resolves with either value or error
 *
 * @example
 * ```ts
 * import { SafePromise } from "@pdfking/tfunc"
 *
 * // Success case
 * const successResult = await SafePromise(Promise.resolve(42));
 * console.log(successResult.value); // 42
 * console.log(successResult.error); // undefined
 *
 * // Error case
 * const errorResult = await SafePromise(Promise.reject(new Error("Failed")));
 * console.log(errorResult.value); // undefined
 * console.log(errorResult.error?.enum); // "INTERNAL_ERROR"
 * console.log(errorResult.error?.error?.message); // "Failed"
 * ```
 */
export const SafePromise = async <T>(promise: Promise<T>): Promise<Result<T>> =>
  Promise.resolve(promise).then(Success).catch(Failure)
